import { OrderDoctypes } from '@/core/constants/orderDoctypeConstants';
import { mapGetters, mapActions } from 'vuex';

export default {
  data() {
    return {
      selectedRecentOrderIdReference: null,
    };
  },

  created() {
    // We save a reference of the current selected order because
    // We have to do some valitations before loading the next selected order
    if (this.selectedRecentOrder) {
      this.selectedRecentOrderIdReference = this.selectedRecentOrderId;
    }
  },

  methods: {
    ...mapActions('ordercart', [
      'loadOrder',
      'resetOrder',
      'loadCartItems',
      'setOrder',
    ]),

    ...mapActions('recentOrders', [
      'setRecentOrderSelected',
      'loadRecentOrders',
      'updateRecentOrderItemsCount',
      'updateRecentOrder',
      'setSelectedOrderAsActivated',
    ]),

    ...mapActions('modalStepsAfterLogin', ['syncSteps']),

    async prepareOrder() {
      this.clearOrder();

      await this.setCustomer();

      if (this.origin === 'recent') {
        await this.loadRecentOrder();
      } else if (this.origin === 'quotation') {
        await this.loadQuotationOrder();
      } else {
        await this.loadNewOrder();
      }

      this.generatePDF();
      this.generateExcel();

      this.launchOrderAlerts();
    },

    async setCustomer() {
      await this.loadCustomer();

      if (this.errorOnLoad) {
        this.$toast.error(
          'Sucedió un error al cargar la información del cliente'
        );
        this.$router.push(this.homeRouteName);
      }

      if (this.customerCode !== this.customer.code) {
        this.customerCode = this.customer.code;

        await this.validateUserBlockedByBackorders();
        await this.getCustomerInfo();
        await this.loadCustomerDestinations({
          custumer_code: this.customerCode,
          VKORG: this.user.VKORG,
          VTWEG: this.user.VTWEG,
          VKBUR: this.user.VKBUR,
        });
      }
    },

    async launchOrderAlerts() {
      this.setShoppingCartAlertsDoctype(this.doctype);
      this.runCartValidations();

      if (this.userIsBlockedByBackorders) {
        await this.launchBackOrdersModal();
        return;
      }

      if (this.cartAlerts.length) {
        await this.launchCartAlertsModal(this.doctype);
      }
    },

    async refreshCart() {
      this.activeLoader();
      await this.prepareOrder();
      this.desactiveLoader();
    },

    async loadRecentOrder() {
      this.setSelectedOrderAsActivated();

      await this.setOrder({
        id: this.selectedRecentOrder.id,
        name: this.selectedRecentOrder.name,
      });

      this.relatedDocs = this.selectedRecentOrder.relatedDoc;
      this.relatedCFDI = this.selectedRecentOrder.relatedCFDI;
      this.destination = this.selectedRecentOrder.destinationId;

      await this.loadItems();
      this.updateCurrentOrder({ reactivated: true });
      this.syncTotal();
    },

    async loadQuotationOrder() {
      await this.loadOrder({
        custumer_code: this.customerCode,
        user_id: this.user.id,
        type: this.userType,
        channel: this.user.VTWEG,
        doctype: this.doctype,
        idq: this.idq,
      });
      await this.loadItems();
    },

    async loadNewOrder() {
      await this.loadOrder({
        custumer_code: this.customerCode,
        user_id: this.user.id,
        type: this.userType,
        channel: this.user.VTWEG,
        doctype: this.doctype,
        idq: 0,
      });
    },

    async loadItems() {
      await this.loadCartItems({
        custumer_code: this.customerCode,
        VKORG: this.user.VKORG,
        VTWEG: this.user.VTWEG,
        VKBUR: this.user.VKBUR,
        doctype: this.doctype,
      });
    },

    async syncConvertedOrder() {
      if (this.origin === 'new' || this.origin === 'recent') {
        const refreshPromise = this.refreshRecentOrders();

        // When the origin is new the order will not be in the list of recent orders;
        // We must wait to set it as a selected order
        if (this.origin === 'new') {
          await refreshPromise;
        }

        await this.setRecentOrderSelected(this.orderId);

        if (this.recentOrdersMutuationError) {
          this.$toast.error(this.recentOrdersMutuationError);
          return false;
        }
      }

      return true;
    },

    async refreshRecentOrders() {
      await this.loadRecentOrders();
      await this.syncSteps();
    },

    async updateCurrentOrder(newData) {
      this.updateRecentOrder({
        id: this.orderId,
        newData,
      });
    },

    async updateOrderTotal() {
      this.updateCurrentOrder({
        total: this.totalCartPrice,
        subtotal: this.subtotalCartPrice,
        tax: this.ivaCartPrice,
      });

      if (this.origin === 'recent' && this.itemsInCart <= 0) {
        this.$toast.warning(
          'Esta orden se eliminará del historial de "Carritos" si no se agregan productos nuevamente.'
        );
      }
    },

    syncTotal() {
      const { total, subtotal, tax } = this.selectedRecentOrder;

      const format = (val) => Number(val).toFixed(2);

      if (
        format(total) !== format(this.totalCartPrice) ||
        format(subtotal) !== format(this.subtotalCartPrice) ||
        format(tax) !== format(this.ivaCartPrice)
      ) {
        this.updateOrderTotal();
      }
    },

    clearOrder() {
      this.resetOrder();
      this.relatedDocs = undefined;
      this.relatedCFDI = undefined;
      this.destination = undefined;
    },

    isScheduleOrder(value) {
      this.scheduleOrder = value;
    },
    isScheduleOrderDate(value) {
      this.scheduleOrderDate = value;
    },
  },

  computed: {
    ...mapGetters('ordercart', ['itemsInCart', 'order']),
    ...mapGetters('selectedUser', ['customer', 'userType', 'errorOnLoad']),

    ...mapGetters('recentOrders', [
      'isRecentOrderSelected',
      'selectedRecentOrder',
      'isRecentOrderSelectedActivated',
      'recentOrdersMutuationError',
    ]),

    idqParsed() {
      return parseInt(this.$route.params?.idq);
    },

    selectedRecentOrderId() {
      return this.selectedRecentOrder?.id || null;
    },

    origin() {
      if (this.selectedRecentOrderIdReference) return 'recent';
      else if (this.idqParsed !== 0) return 'quotation';
      else return 'new';
    },

    doctype() {
      return this.$route.params.doctype;
    },

    idq() {
      if (this.origin === 'recent') {
        return this.selectedRecentOrder.id;
      } else {
        return this.idqParsed;
      }
    },

    isNewOrderDoctype() {
      return (
        this.doctype === OrderDoctypes.PRIVATE_CHANNEL ||
        this.doctype === OrderDoctypes.RETAIL_CHANNEL
      );
    },
  },

  watch: {
    // Triggered when the user selects, unselects, or changes the selected order in the "Carritos" modal
    async selectedRecentOrderId(newVal) {
      const sameDoctype = this.selectedRecentOrder?.doctype === this.doctype;

      // If the user is creating a new order and has already added items,
      // reload the recent orders so the new one appears in the "Carritos" modal
      if (this.origin === 'new' && this.itemsInCart > 0) {
        this.refreshRecentOrders();
      }

      // If the selected order has the same doctype and it's not a quotation,
      // update the reference and refresh the cart manually.
      // Otherwise (different doctype or quotation), the full view will be remounted.
      // newVal = null means that the user unselect the order, so we refresh with a new empty cart
      if ((sameDoctype && this.origin !== 'quotation') || newVal === null) {
        this.selectedRecentOrderIdReference = newVal;

        this.refreshCart();
      }
    },

    itemsInCart(newCount) {
      if (this.origin === 'recent') {
        this.updateRecentOrderItemsCount({
          orderId: this.selectedRecentOrderIdReference,
          newCount,
        });
      }
    },
  },

  // Prevent refreshing the recenOrders is user is logging out
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'Logout') {
      this.refreshRecentOrders();
    }
    next();
  },

  async beforeDestroy() {
    if (this.isRecentOrderSelected && this.isRecentOrderSelectedActivated) {
      this.setRecentOrderSelected(null);
    }

    this.clearOrder();
  },
};
